<template>
  <div class="mt-6 px-4">
    <div class="flex justify-between items-center w-full">
      <div class="flex items-start">
        <span class="flex-shrink-0 text-green-500" style="margin-inline-end: 12px; margin-top: 4px;">
          <Icon icon="mdi:whatsapp" class="w-9 h-9" />
        </span>
        <div>
          <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('whatsappTemplates.title') }}</h1>
          <p class="text text-gray-900 dark:text-gray-200">{{ t('whatsappTemplates.subtitle') }}</p>
        </div>
      </div>
      <router-link to="/communicationSources" class="btn">
        {{ t('whatsappTemplates.communicationSourcesButton') }}
      </router-link>
    </div>

    <div class="card shadow bg-white dark:bg-gray-800 w-full rounded-md p-4 border dark:border-gray-700 mt-6">
      <div class="flex flex-wrap items-end">
        <div class="w-full md:w-96" style="margin-inline-end: 12px; margin-bottom: 12px;">
          <label class="label">
            <span class="label-text">{{ t('whatsappTemplates.sourceLabel') }}</span>
          </label>
          <select v-model="selectedSourceId" class="select select-bordered w-full" @change="handleSourceChange">
            <option value="">{{ t('whatsappTemplates.selectSource') }}</option>
            <option v-for="source in sources" :key="source.sourceId" :value="String(source.sourceId)">
              {{ source.display }}{{ source.phoneNumber !== '' ? ` (${source.phoneNumber})` : '' }}
            </option>
          </select>
        </div>
        <div style="margin-bottom: 12px;">
          <button class="btn" :disabled="loadingTemplates === true || selectedSourceId === ''" @click="loadTemplates">
            {{ loadingTemplates === true ? t('whatsappTemplates.refreshing') : t('whatsappTemplates.refreshButton') }}
          </button>
        </div>
      </div>

      <div v-if="loadingSources === true" class="py-8 text-center text-sm text-gray-500">
        {{ t('whatsappTemplates.loadingSources') }}
      </div>

      <div v-else-if="sources.length === 0" class="py-10 text-center text-gray-500">
        <div class="text-lg font-medium">{{ t('whatsappTemplates.noSourcesTitle') }}</div>
        <div class="mt-2">{{ t('whatsappTemplates.noSourcesBody') }}</div>
      </div>

      <div v-else-if="selectedSourceId === ''" class="py-10 text-center text-gray-500">
        <div class="text-lg font-medium">{{ t('whatsappTemplates.chooseSourceTitle') }}</div>
        <div class="mt-2">{{ t('whatsappTemplates.chooseSourceBody') }}</div>
      </div>

      <div v-else>
        <div class="grid grid-cols-1 lg:grid-cols-2 mt-4">
          <div class="card bg-base-200" style="margin-inline-end: 12px; margin-bottom: 12px;">
            <div class="card-body">
              <h2 class="card-title flex items-center">
                <Icon icon="mdi:whatsapp" class="w-7 h-7 text-green-500 flex-shrink-0" style="margin-inline-end: 8px;" />
                {{ t('whatsappTemplates.listTitle') }}
              </h2>
              <div v-if="syncError !== ''" class="alert alert-warning mb-4">
                <span>{{ syncError }}</span>
              </div>
              <div v-if="loadingTemplates === true" class="py-10 text-center text-sm text-gray-500">
                {{ t('whatsappTemplates.loadingTemplates') }}
              </div>
              <div v-else-if="templates.length === 0" class="py-10 text-center text-gray-500">
                <div class="text-lg font-medium">{{ t('whatsappTemplates.noTemplatesTitle') }}</div>
                <div class="mt-2">{{ t('whatsappTemplates.noTemplatesBody') }}</div>
              </div>
              <div v-else class="whatsapp-templates-scroll overflow-y-auto pl-4" style="min-height: 48rem; max-height: min(82vh, 52rem);">
                <div
                  v-for="template in templates"
                  :key="template.templateId"
                  class="card bg-base-100 border border-base-300 shadow-sm mb-3"
                >
                  <div class="card-body" style="padding: 14px;">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center justify-start text-xs template-card-tags-row" style="margin-bottom: 8px;">
                        <span class="badge" :class="getStatusBadgeClass(template.status)" style="margin-inline-end: 8px; margin-bottom: 4px;">{{ template.status }}</span>
                        <span class="badge badge-outline uppercase" style="margin-inline-end: 8px; margin-bottom: 4px;">{{ template.category }}</span>
                      </div>
                      <div class="font-semibold text-base-content break-words">{{ template.templateName }}</div>
                      <div class="text-xs text-base-content/60 mt-1">{{ t('whatsappTemplates.cardPreview') }}</div>
                      <div class="text-sm text-base-content/80 mt-1 line-clamp-3 break-words">{{ template.bodyText }}</div>
                      <div class="flex flex-wrap items-center mt-3 text-xs">
                        <span class="inline-flex items-center" style="margin-inline-end: 10px; margin-bottom: 4px;">
                          <span class="text-base leading-none" aria-hidden="true">{{ regionToFlagEmoji(getRegionForLanguageCode(template.languageCode)) }}</span>
                          <span class="ms-1.5 font-mono">{{ template.languageCode }}</span>
                        </span>
                      </div>
                      <div class="text-xs text-base-content/50 mt-2">{{ formatDate(template.updatedAt) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card bg-base-200" style="margin-bottom: 12px;">
            <div class="card-body">
              <h2 class="card-title">{{ t('whatsappTemplates.createTitle') }}</h2>
              <div class="w-full">
                <label class="label"><span class="label-text">{{ t('whatsappTemplates.nameLabel') }}</span></label>
                <input v-model="form.templateName" type="text" class="input input-bordered w-full" placeholder="order_update" />
              </div>
              <div class="flex flex-wrap">
                <div class="w-full md:w-1/2" style="margin-inline-end: 12px; margin-bottom: 12px;">
                  <label class="label"><span class="label-text">{{ t('whatsappTemplates.languageLabel') }}</span></label>
                  <select v-model="form.languageCode" class="select select-bordered w-full">
                    <option v-for="opt in localeSelectOptions" :key="opt.code" :value="opt.code">
                      {{ regionToFlagEmoji(opt.region) }} {{ getLocaleLabel(opt.code) }}
                    </option>
                  </select>
                </div>
                <div class="w-full md:flex-1" style="margin-bottom: 12px;">
                  <label class="label"><span class="label-text">{{ t('whatsappTemplates.categoryLabel') }}</span></label>
                  <select v-model="form.category" class="select select-bordered w-full">
                    <option value="utility">{{ t('whatsappTemplates.categoryUtility') }}</option>
                    <option value="marketing">{{ t('whatsappTemplates.categoryMarketing') }}</option>
                    <option value="authentication">{{ t('whatsappTemplates.categoryAuthentication') }}</option>
                  </select>
                </div>
              </div>
              <div class="w-full">
                <label class="label"><span class="label-text">{{ t('whatsappTemplates.bodyLabel') }}</span></label>
                <div class="template-body-editor" :dir="bodyTextDirection">
                  <textarea
                    v-model="form.bodyText"
                    class="textarea textarea-bordered w-full template-body-textarea"
                    rows="6"
                    placeholder="Hi {{1}}, your order {{2}} is ready."
                    :dir="bodyTextDirection"
                    @scroll="syncBodyHighlightScroll"
                  ></textarea>
                  <div class="template-body-highlight-layer" aria-hidden="true">
                    <div
                      class="template-body-highlight-content"
                      :dir="bodyTextDirection"
                      :style="{ transform: `translate(${-bodyHighlightOffset.left}px, ${-bodyHighlightOffset.top}px)` }"
                      v-html="highlightedBodyHtml"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="w-full">
                <label class="label"><span class="label-text">{{ t('whatsappTemplates.footerLabel') }}</span></label>
                <input v-model="form.footerText" type="text" class="input input-bordered w-full" :placeholder="t('whatsappTemplates.footerPlaceholder')" />
              </div>
              <div class="w-full">
                <label class="label"><span class="label-text">{{ t('whatsappTemplates.examplesLabel') }}</span></label>
                <input v-model="form.bodyExamplesText" type="text" class="input input-bordered w-full" :placeholder="requiredExampleCount > 0 ? t('whatsappTemplates.examplesPlaceholderFilled') : t('whatsappTemplates.examplesPlaceholderEmpty')" />
                <div class="text-xs text-gray-500 mt-2">
                  {{ requiredExampleCount > 0 ? t('whatsappTemplates.examplesHintRequired', { count: String(requiredExampleCount) }) : t('whatsappTemplates.examplesHintOptional') }}
                </div>
              </div>
              <div class="card-actions justify-end mt-4">
                <button class="btn btn-primary" :disabled="saving === true" @click="createTemplate">
                  {{ saving === true ? t('whatsappTemplates.creating') : t('whatsappTemplates.createButton') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n.js';
import { Icon } from '@iconify/vue';

const toast = useToast();
const { t } = useI18n();

const metaLocaleOptions = [
  { code: 'en_US', region: 'US' },
  { code: 'en_GB', region: 'GB' },
  { code: 'he', region: 'IL' },
  { code: 'es', region: 'ES' },
  { code: 'es_ES', region: 'ES' },
  { code: 'es_MX', region: 'MX' },
  { code: 'es_AR', region: 'AR' },
  { code: 'fr', region: 'FR' },
  { code: 'de', region: 'DE' },
  { code: 'pt_BR', region: 'BR' },
  { code: 'it', region: 'IT' },
  { code: 'ar', region: 'SA' },
  { code: 'hi', region: 'IN' },
  { code: 'ru', region: 'RU' },
  { code: 'ja', region: 'JP' },
  { code: 'ko', region: 'KR' },
  { code: 'zh_CN', region: 'CN' },
  { code: 'nl', region: 'NL' },
  { code: 'pl', region: 'PL' },
  { code: 'tr', region: 'TR' },
  { code: 'uk', region: 'UA' },
  { code: 'id', region: 'ID' },
  { code: 'th', region: 'TH' },
  { code: 'vi', region: 'VN' },
  { code: 'ms', region: 'MY' }
];

const sources = ref([]);
const templates = ref([]);
const selectedSourceId = ref('');
const loadingSources = ref(false);
const loadingTemplates = ref(false);
const saving = ref(false);
const syncError = ref('');
const bodyHighlightOffset = ref({ top: 0, left: 0 });

const form = reactive({
  templateName: '',
  languageCode: 'en_US',
  category: 'utility',
  bodyText: '',
  footerText: '',
  bodyExamplesText: ''
});

function regionToFlagEmoji(region) {
  if (typeof region !== 'string' || region.length !== 2) {
    return '\u{1F310}';
  }
  const upper = region.toUpperCase();
  if (upper === 'UN') {
    return '\u{1F310}';
  }
  const base = 0x1F1E6;
  const c1 = upper.charCodeAt(0) - 65;
  const c2 = upper.charCodeAt(1) - 65;
  if (c1 < 0 || c1 > 25 || c2 < 0 || c2 > 25) {
    return '\u{1F310}';
  }
  return String.fromCodePoint(base + c1, base + c2);
}

function inferRegionFromLocale(code) {
  if (typeof code !== 'string' || code.length === 0) {
    return 'UN';
  }
  const upper = code.toUpperCase();
  if (upper === 'HE' || upper.indexOf('HE_') === 0) {
    return 'IL';
  }
  if (upper === 'AR' && upper.length === 2) {
    return 'SA';
  }
  const underscore = code.indexOf('_');
  if (underscore > 0) {
    const tail = code.slice(underscore + 1);
    if (tail.length === 2) {
      return tail.toUpperCase();
    }
  }
  return 'UN';
}

function getRegionForLanguageCode(code) {
  if (typeof code !== 'string' || code.length === 0) {
    return 'UN';
  }
  for (let i = 0; i < metaLocaleOptions.length; i = i + 1) {
    if (metaLocaleOptions[i].code === code) {
      return metaLocaleOptions[i].region;
    }
  }
  return inferRegionFromLocale(code);
}

const localeSelectOptions = computed(() => {
  const rows = [];
  const seen = new Set();
  for (let i = 0; i < metaLocaleOptions.length; i = i + 1) {
    rows.push(metaLocaleOptions[i]);
    seen.add(metaLocaleOptions[i].code);
  }
  const current = form.languageCode;
  if (typeof current === 'string' && current.length > 0 && seen.has(current) === false) {
    rows.unshift({ code: current, region: inferRegionFromLocale(current) });
  }
  return rows;
});

const highlightedBodyHtml = computed(() => buildHighlightedBodyHtml(form.bodyText));
const bodyTextDirection = computed(() => getBodyTextDirection(form.bodyText, form.languageCode));

function getLocaleLabel(code) {
  const key = `whatsappTemplates.localeLabels.${code}`;
  return t(key, code);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildHighlightedBodyHtml(value) {
  const text = typeof value === 'string' ? value : '';
  if (text.length === 0) {
    return '';
  }
  const pattern = /\{\{[^{}]*\}\}/g;
  let output = '';
  let lastIndex = 0;
  let match = pattern.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      output += escapeHtml(text.slice(lastIndex, match.index));
    }
    output += `<mark class="template-variable-highlight" dir="ltr">${escapeHtml(match[0])}</mark>`;
    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (lastIndex < text.length) {
    output += escapeHtml(text.slice(lastIndex));
  }
  return output;
}

function getBodyTextDirection(text, languageCode) {
  const source = typeof text === 'string' ? text : '';
  for (let index = 0; index < source.length; index = index + 1) {
    const char = source[index];
    if (/[A-Za-z]/.test(char) === true) {
      return 'ltr';
    }
    if (/[\u0590-\u05FF\u0600-\u06FF]/.test(char) === true) {
      return 'rtl';
    }
  }
  const lang = typeof languageCode === 'string' ? languageCode.toLowerCase() : '';
  if (lang === 'he' || lang.indexOf('he_') === 0 || lang === 'ar' || lang.indexOf('ar_') === 0) {
    return 'rtl';
  }
  return 'ltr';
}

function extractPlaceholderCount(text) {
  const source = typeof text === 'string' ? text : '';
  const matches = source.match(/\{\{\s*\d+\s*\}\}/g);
  if (Array.isArray(matches) !== true || matches.length === 0) {
    return 0;
  }
  let maxValue = 0;
  for (let index = 0; index < matches.length; index = index + 1) {
    const parsed = Number.parseInt(matches[index].replace(/[^\d]/g, ''), 10);
    if (Number.isFinite(parsed) === true && parsed > maxValue) {
      maxValue = parsed;
    }
  }
  return maxValue;
}

const requiredExampleCount = computed(() => extractPlaceholderCount(form.bodyText));

function getBodyExamples() {
  if (typeof form.bodyExamplesText !== 'string' || form.bodyExamplesText.trim().length === 0) {
    return [];
  }
  return form.bodyExamplesText.split(',').map((value) => value.trim()).filter((value) => value.length > 0);
}

function syncBodyHighlightScroll(event) {
  const target = event?.target;
  if (target === undefined || target === null) {
    return;
  }
  bodyHighlightOffset.value = {
    top: target.scrollTop,
    left: target.scrollLeft
  };
}

function resetForm() {
  form.templateName = '';
  form.languageCode = 'en_US';
  form.category = 'utility';
  form.bodyText = '';
  form.footerText = '';
  form.bodyExamplesText = '';
}

function formatDate(value) {
  if (value === undefined || value === null || String(value).trim().length === 0) {
    return '';
  }
  return new Date(value).toLocaleString();
}

function getStatusBadgeClass(status) {
  const normalizedStatus = typeof status === 'string' ? status.trim().toUpperCase() : '';
  if (normalizedStatus === 'APPROVED') {
    return 'badge-success';
  }
  if (normalizedStatus === 'REJECTED' || normalizedStatus === 'DISABLED' || normalizedStatus === 'PAUSED') {
    return 'badge-error';
  }
  return 'badge-warning';
}

async function loadSources() {
  loadingSources.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/whatsappMeta/sources`);
    sources.value = Array.isArray(response.data) === true ? response.data : [];
    if (selectedSourceId.value === '' && sources.value.length > 0) {
      selectedSourceId.value = String(sources.value[0].sourceId);
    }
  } catch (error) {
    toast.error(t('whatsappTemplates.failedToLoadSources'));
  } finally {
    loadingSources.value = false;
  }
}

async function loadTemplates() {
  if (selectedSourceId.value === '') {
    templates.value = [];
    syncError.value = '';
    return;
  }
  loadingTemplates.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/communicationSources/whatsappMeta/templates`, {
      params: {
        sourceId: selectedSourceId.value
      }
    });
    templates.value = Array.isArray(response.data?.items) === true ? response.data.items : [];
    syncError.value = typeof response.data?.syncError === 'string' ? response.data.syncError : '';
  } catch (error) {
    templates.value = [];
    syncError.value = '';
    const apiError = typeof error?.response?.data?.error === 'string' ? error.response.data.error : '';
    toast.error(apiError !== '' ? apiError : t('whatsappTemplates.failedToLoadTemplates'));
  } finally {
    loadingTemplates.value = false;
  }
}

async function handleSourceChange() {
  await loadTemplates();
}

async function createTemplate() {
  const normalizedSourceId = Number.parseInt(selectedSourceId.value, 10);
  if (Number.isFinite(normalizedSourceId) !== true || normalizedSourceId <= 0) {
    toast.error(t('whatsappTemplates.sourceRequired'));
    return;
  }
  if (typeof form.templateName !== 'string' || form.templateName.trim().length === 0) {
    toast.error(t('whatsappTemplates.nameRequired'));
    return;
  }
  if (typeof form.languageCode !== 'string' || form.languageCode.trim().length === 0) {
    toast.error(t('whatsappTemplates.languageRequired'));
    return;
  }
  if (typeof form.bodyText !== 'string' || form.bodyText.trim().length === 0) {
    toast.error(t('whatsappTemplates.bodyRequired'));
    return;
  }
  const bodyExamples = getBodyExamples();
  if (requiredExampleCount.value > 0 && bodyExamples.length < requiredExampleCount.value) {
    toast.error(t('whatsappTemplates.examplesRequired', { count: String(requiredExampleCount.value) }));
    return;
  }
  saving.value = true;
  try {
    await axios.post(`${globalState.apiEndpoint}/api/communicationSources/whatsappMeta/templates`, {
      sourceId: normalizedSourceId,
      templateName: form.templateName,
      languageCode: form.languageCode,
      category: form.category,
      bodyText: form.bodyText,
      footerText: form.footerText,
      bodyExamples
    });
    toast.success(t('whatsappTemplates.createdSuccess'));
    resetForm();
    await loadTemplates();
  } catch (error) {
    const apiError = typeof error?.response?.data?.error === 'string' ? error.response.data.error : '';
    const apiDetails = typeof error?.response?.data?.details === 'string' ? error.response.data.details : '';
    const message = apiError !== '' ? `${apiError}${apiDetails !== '' ? `: ${apiDetails}` : ''}` : t('whatsappTemplates.createFailed');
    toast.error(message);
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await loadSources();
  await loadTemplates();
});
</script>

<style scoped>
.whatsapp-templates-scroll {
  scrollbar-gutter: stable;
  scrollbar-width: auto;
  scrollbar-color: rgba(100, 116, 139, 0.55) rgba(0, 0, 0, 0.08);
}
.whatsapp-templates-scroll::-webkit-scrollbar {
  width: 14px;
}
.whatsapp-templates-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.08);
  border-radius: 10px;
}
html.dark .whatsapp-templates-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}
.whatsapp-templates-scroll::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.55);
  border-radius: 10px;
  min-height: 72px;
}
.whatsapp-templates-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.8);
}
.template-body-editor {
  position: relative;
}
.template-body-textarea {
  position: relative;
  z-index: 2;
  background: #ffffff;
  color: #111827;
  caret-color: #111827;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: normal;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  text-align: start;
  unicode-bidi: plaintext;
}
.template-body-textarea::placeholder {
  color: #9ca3af;
}
html.dark .template-body-textarea {
  background: #ffffff;
  caret-color: #111827;
}
.template-body-highlight-layer {
  position: absolute;
  inset: 1px;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
  border-radius: 0.5rem;
}
.template-body-highlight-content {
  min-height: 100%;
  padding: 0.75rem 1rem;
  color: #111827;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: normal;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: start;
  unicode-bidi: plaintext;
  box-sizing: border-box;
}
html.dark .template-body-highlight-content {
  color: #111827;
}
.template-variable-highlight {
  background: rgba(250, 204, 21, 0.75);
  color: #111827;
  border-radius: 0.25rem;
  padding: 0.02rem 0;
  direction: ltr;
  unicode-bidi: isolate;
}
html.dark .template-variable-highlight {
  background: rgba(250, 204, 21, 0.5);
}
.template-card-tags-row {
  direction: ltr;
}
</style>
